import React, { useRef } from "react";
import Draggable from "react-draggable";
import { X, MapPin, Copy, Check } from "lucide-react";
import { LOCATION_ID_TO_NAME } from "../../utils/constants";
import useSettingsStore from "../../stores/settingsStore";

const EventDetailModal = ({ event, userIdToFirstName, onClose }) => {
    const dragRef = useRef(null);
    const [copiedField, setCopiedField] = React.useState(null);
    const piiClass = useSettingsStore(s => s.blurPII) ? 'blur-pii' : '';

    if (!event) return null;

    const copyToClipboard = (value, fieldName) => {
        navigator.clipboard.writeText(String(value));
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const hasGeo = event.latitude != null && event.longitude != null;

    const formatTimestamp = (ts) => {
        if (!ts) return "—";
        const d = new Date(ts);
        return d.toLocaleString("en-US", {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            timeZoneName: "short",
        });
    };

    const DetailRow = ({ label, value, mono = false, copyable = false }) => (
        <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-36 shrink-0 pt-0.5">{label}</span>
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <span className={`text-sm text-gray-700 break-all ${mono ? "font-mono text-xs" : ""}`}>
                    {value ?? "—"}
                </span>
                {copyable && value && (
                    <button
                        onClick={() => copyToClipboard(value, label)}
                        className="!p-1 !bg-transparent !border-none !text-gray-300 hover:!text-gray-500 !transition-colors shrink-0"
                        title="Copy"
                    >
                        {copiedField === label ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                    </button>
                )}
            </div>
        </div>
    );

    const eventTypeBadge = (type) => {
        const base = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide";
        switch (type) {
            case "clock in": return `${base} bg-emerald-100 text-emerald-700`;
            case "clock out": return `${base} bg-rose-100 text-rose-700`;
            case "sick": return `${base} bg-purple-100 text-purple-700`;
            case "time off": return `${base} bg-blue-100 text-blue-700`;
            case "note": return `${base} bg-amber-100 text-amber-700`;
            case "comped service": return `${base} bg-teal-100 text-teal-700`;
            default: return `${base} bg-gray-100 text-gray-600`;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Draggable handle=".modal-drag-handle" nodeRef={dragRef}>
                <div
                    ref={dragRef}
                    className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
                    style={{ cursor: "default" }}
                >
                    {/* Header */}
                    <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 modal-drag-handle cursor-move select-none">
                        <h2 className="text-lg font-bold text-gray-800">Event Details</h2>
                        <button
                            onClick={onClose}
                            className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="overflow-y-auto flex-grow px-6 py-4">
                        {/* Event type badge */}
                        <div className="mb-4">
                            <span className={eventTypeBadge(event.event_type)}>{event.event_type}</span>
                        </div>

                        {/* Core fields */}
                        <div className="mb-5">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Identity</h3>
                            <DetailRow label="UUID" value={event.id} mono copyable />
                            <DetailRow label="User" value={<span className={piiClass}>{userIdToFirstName?.[event.user_id] || "Unknown"}</span>} />
                            <DetailRow label="Created By" value={event.created_by ? <span className={piiClass}>{userIdToFirstName?.[event.created_by] || "Unknown"}</span> : "—"} />
                        </div>

                        {/* Timing */}
                        <div className="mb-5">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Timing</h3>
                            <DetailRow label="Event Time" value={formatTimestamp(event.event_timestamp)} />
                            <DetailRow label="Epoch (ms)" value={event.event_timestamp ? new Date(event.event_timestamp).getTime() : null} mono copyable />
                            {event.event_end_timestamp && (
                                <DetailRow label="End Time" value={formatTimestamp(event.event_end_timestamp)} />
                            )}
                            <DetailRow label="Insert Time" value={formatTimestamp(event.insert_timestamp)} />
                        </div>

                        {/* Classification */}
                        <div className="mb-5">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Classification</h3>
                            <DetailRow label="Location" value={`${LOCATION_ID_TO_NAME[event.location_id] || "Unknown"} (ID: ${event.location_id})`} />
                            <DetailRow label="Sequence #" value={event.sequence_number} />
                            <DetailRow label="Value" value={event.value != null ? `$${event.value}` : null} />
                            <DetailRow label="Needs Review" value={event.needs_review ? "Yes" : "No"} />
                            <DetailRow label="Forgotten Event" value={event.is_forgotten_event ? "Yes" : "No"} />
                        </div>

                        {/* Notes */}
                        {event.notes && (
                            <div className="mb-5">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Notes</h3>
                                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                    {event.notes}
                                </div>
                            </div>
                        )}

                        {/* Origin */}
                        <div className="mb-5">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Origin</h3>
                            <DetailRow label="IP Address" value={event.ip_address} mono copyable />
                            {hasGeo ? (
                                <>
                                    <DetailRow label="Latitude" value={event.latitude} mono copyable />
                                    <DetailRow label="Longitude" value={event.longitude} mono copyable />
                                    <div className="mt-2">
                                        <a
                                            href={`https://www.google.com/maps?q=${event.latitude},${event.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                            <MapPin size={13} />
                                            View on Google Maps
                                        </a>
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-gray-400 italic">No geolocation data recorded for this event.</p>
                            )}
                        </div>

                        {/* Reviews */}
                        {Array.isArray(event.reviews) && event.reviews.length > 0 && (
                            <div className="mb-5">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Reviews</h3>
                                {event.reviews.map((review, idx) => (
                                    <div key={idx} className="bg-gray-50 rounded-lg p-3 mb-2 text-sm">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${review.decision ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                                {review.decision ? "Approved" : "Denied"}
                                            </span>
                                            <span className={`text-xs text-gray-500 ${piiClass}`}>
                                                by {userIdToFirstName?.[review.reviewer_id] || `User ${review.reviewer_id}`}
                                            </span>
                                        </div>
                                        {review.decision_timestamp && (
                                            <div className="text-xs text-gray-400">{formatTimestamp(review.decision_timestamp)}</div>
                                        )}
                                        {review.note && <div className="text-xs text-gray-600 mt-1">{review.note}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

export default EventDetailModal;
